import React, { useState } from 'react';
import { Plane, Car, Users, CheckCircle2, AlertCircle, Clock, CreditCard, UserCheck, Bus, Luggage, ArrowLeft, ArrowRight, ArrowRightLeft, MapPin, Banknote, Landmark, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { TrustRow, WhyChoose, OurServices, GoogleReviews } from '../components/SharedSections';
import { transfersFaq as transfersFaqItems } from '../data/faq';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const benefits = [
  {
    icon: Plane,
    titleRo: 'Monitorizare Zboruri în Timp Real',
    titleEn: 'Real-Time Flight Monitoring',
    descRo: 'Monitorizăm în timp real ora de aterizare a zborului și adaptăm preluarea fără costuri suplimentare, astfel încât șoferul să fie prezent exact atunci când ai nevoie.',
    descEn: 'We monitor your flight landing time in real time and adjust the pickup at no extra cost, so the driver is there exactly when you need them.',
  },
  {
    icon: Clock,
    titleRo: 'Prima Oră de Așteptare Gratuită',
    titleEn: 'First Hour of Waiting Free',
    descRo: 'Beneficiezi de prima oră de așteptare inclusă în preț, pentru ca întârzierile de zbor sau formalitățile din aeroport să nu îți afecteze transferul.',
    descEn: 'The first hour of waiting is included in the price, so flight delays or airport formalities won\'t affect your transfer.',
  },
  {
    icon: Car,
    titleRo: 'Autoturisme Moderne Premium',
    titleEn: 'Modern Premium Vehicles',
    descRo: 'Flotă modernă, întreținută impecabil și echipată pentru confort maxim, oferind o experiență sigură și plăcută indiferent de destinație.',
    descEn: 'Modern fleet, impeccably maintained and equipped for maximum comfort, providing a safe and pleasant experience regardless of destination.',
  },
  {
    icon: Luggage,
    titleRo: 'Spațiu Generos pentru Bagaje',
    titleEn: 'Generous Luggage Space',
    descRo: 'Vehiculele noastre oferă suficient spațiu pentru bagajele întregului grup, inclusiv bagaje voluminoase sau echipamente speciale.',
    descEn: 'Our vehicles offer ample space for the entire group\'s luggage, including bulky bags or special equipment.',
  },
  {
    icon: MapPin,
    titleRo: 'Transferuri Oriunde în România',
    titleEn: 'Transfers Anywhere in Romania',
    descRo: 'Asigurăm transferuri private către Aeroportul Brașov-Ghimbav, Otopeni, Băneasa, Sibiu, Cluj-Napoca și orice alt aeroport sau destinație din România.',
    descEn: 'We provide private transfers to Brașov-Ghimbav Airport, Otopeni, Băneasa, Sibiu, Cluj-Napoca and any other airport or destination in Romania.',
  },
  {
    icon: Star,
    titleRo: 'Punctualitate și Profesionalism',
    titleEn: 'Punctuality & Professionalism',
    descRo: 'Respectăm fiecare rezervare cu seriozitate, șoferi profesioniști și servicii orientate către confortul și timpul fiecărui client.',
    descEn: 'We treat every booking with seriousness, professional drivers and services oriented towards the comfort and time of every client.',
  },
];

const carTypes = [
  { id: 'standard', label: 'STANDARD', desc: 'Skoda Superb', descFull: 'max 3 persoane', descFullEn: 'max 3 persons', maxPax: 3 },
  { id: 'premium', label: 'PREMIUM', desc: 'Mercedes E300de', descFull: 'max 3 persoane', descFullEn: 'max 3 persons', maxPax: 3 },
  { id: 'minivan', label: 'MINIVAN 7+1', desc: 'Mercedes Vito Tourer', descFull: 'max 7 persoane', descFullEn: 'max 7 persons', maxPax: 7 },
];

const airports = [
  { id: 'ghv', nameRo: 'Aeroportul Internațional Brașov-Ghimbav (GHV)', nameEn: 'Brașov-Ghimbav International Airport (GHV)', code: 'GHV', prices: { standard: 150, premium: 200, minivan: 200 } },
  { id: 'otp', nameRo: 'Aeroportul Internațional Henri Coandă (OTP)', nameEn: 'Henri Coandă International Airport (OTP)', code: 'OTP', prices: { standard: 650, premium: 800, minivan: 850 } },
  { id: 'bbu', nameRo: 'Aeroportul Internațional București - Băneasa (BBU)', nameEn: 'Bucharest Băneasa International Airport (BBU)', code: 'BBU', prices: { standard: 700, premium: 850, minivan: 900 } },
  { id: 'sbz', nameRo: 'Aeroportul Internațional Sibiu (SBZ)', nameEn: 'Sibiu International Airport (SBZ)', code: 'SBZ', prices: { standard: 650, premium: 800, minivan: 850 } },
  { id: 'tgm', nameRo: 'Aeroportul Internațional Târgu-Mureș (TGM)', nameEn: 'Târgu-Mureș International Airport (TGM)', code: 'TGM', prices: { standard: 750, premium: 900, minivan: 950 } },
  { id: 'clj', nameRo: 'Aeroportul Internațional Cluj-Napoca (CLJ)', nameEn: 'Cluj-Napoca International Airport (CLJ)', code: 'CLJ', prices: { standard: 1200, premium: 1500, minivan: 1600 } },
];


const TransfersFAQ = ({ isRo }) => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <section className="py-20 sm:py-28 bg-white" data-testid="transfers-faq">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-12 sm:mb-16">
          <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">FAQ</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0D1B3E]">
            {isRo ? 'Întrebări Frecvente' : 'Frequently Asked Questions'}
          </h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-3">
          {transfersFaqItems.map((item, i) => (
            <div key={i} className="rounded-xl overflow-hidden transition-all"
              style={{ border: '1px solid rgba(13,27,62,0.08)', backgroundColor: openIndex === i ? '#F8FAFC' : '#fff' }}
              data-testid={`transfers-faq-${i}`}>
              <button onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left cursor-pointer">
                <span className="font-display text-sm sm:text-base font-semibold text-[#0D1B3E] pr-4">
                  {isRo ? item.qRo : item.qEn}
                </span>
                <svg className={`w-5 h-5 text-[#10B981] flex-shrink-0 transition-transform ${openIndex === i ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openIndex === i && (
                <div className="px-5 pb-5">
                  <p className="font-body text-sm text-gray-600 leading-relaxed">
                    {isRo ? item.aRo : item.aEn}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const slidesData = [
  {
    id: 'business',
    titleRo: 'Transferuri Business',
    titleEn: 'Business Transfers',
    descRo: 'Asigurăm transport premium pentru întâlniri de afaceri, delegații, conferințe și vizite corporate, cu accent pe punctualitate, discreție și confort. O alegere ideală pentru profesioniști și companii care apreciază serviciile de calitate.',
    descEn: 'We provide premium transport for business meetings, delegations, conferences and corporate visits, with a focus on punctuality, discretion and comfort. An ideal choice for professionals and companies who value quality services.',
    ctaRo: 'Solicită Ofertă',
    ctaEn: 'Request a Quote',
    image: '/images/slide-business.webp',
  },
  {
    id: 'corporate',
    titleRo: 'Soluții Corporate',
    titleEn: 'Corporate Solutions',
    descRo: 'Colaborăm cu companii care au nevoie de transferuri regulate pentru angajați, parteneri și clienți VIP. Oferim colaborări pe termen lung, facturare simplificată și servicii adaptate nevoilor fiecărei organizații.',
    descEn: 'We partner with companies that need regular transfers for employees, partners and VIP clients. We offer long-term collaborations, simplified invoicing and services adapted to each organization\'s needs.',
    ctaRo: 'Devino Partener',
    ctaEn: 'Become a Partner',
    image: '/images/slide-corporate.webp',
  },
  {
    id: 'events',
    titleRo: 'Evenimente Private',
    titleEn: 'Private Events',
    descRo: 'Transport dedicat pentru nunți, conferințe, concerte, festivaluri și evenimente private. Ne ocupăm de deplasarea invitaților în siguranță, confort și la timp, astfel încât tu să te concentrezi pe eveniment.',
    descEn: 'Dedicated transport for weddings, conferences, concerts, festivals and private events. We handle the transportation of guests safely, comfortably and on time, so you can focus on your event.',
    ctaRo: 'Solicită Ofertă',
    ctaEn: 'Request a Quote',
    image: '/images/slide-events.webp',
  },
];

const TransferSlides = ({ isRo }) => {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);

  const goToContact = () => {
    navigate('/contact');
  };

  return (
    <section className="py-20 sm:py-28" style={{ backgroundColor: '#F8FAFC' }} data-testid="transfer-slides">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-12 sm:mb-16">
          <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
            {isRo ? 'Servicii Dedicate' : 'Dedicated Services'}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0D1B3E]">
            {isRo ? 'Soluții pentru fiecare nevoie' : 'Solutions for every need'}
          </h2>
        </div>

        {/* Desktop: 3 cards side by side */}
        <div className="hidden md:grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {slidesData.map((slide) => (
            <div key={slide.id} className="rounded-xl overflow-hidden flex flex-col transition-all hover:shadow-xl group"
              data-testid={`slide-${slide.id}`}
              style={{ backgroundColor: '#fff', border: '1px solid rgba(13,27,62,0.08)', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
              <div className="relative h-52 overflow-hidden">
                <img src={slide.image} alt={isRo ? slide.titleRo : slide.titleEn} loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(13,27,62,0.5) 0%, transparent 60%)' }}></div>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-display text-lg font-bold text-[#0D1B3E] mb-3">
                  {isRo ? slide.titleRo : slide.titleEn}
                </h3>
                <p className="font-body text-sm text-gray-500 leading-relaxed mb-6 flex-1">
                  {isRo ? slide.descRo : slide.descEn}
                </p>
                <button onClick={goToContact}
                  data-testid={`slide-cta-${slide.id}`}
                  className="inline-flex items-center justify-center bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold px-6 h-12 text-sm rounded shadow-md hover:shadow-lg transition-all cursor-pointer w-full">
                  {isRo ? slide.ctaRo : slide.ctaEn}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: Swipeable single slide carousel */}
        <div className="md:hidden">
          <div
            className="overflow-hidden rounded-xl touch-pan-y"
            onTouchStart={(e) => {
              e.currentTarget._touchStartX = e.touches[0].clientX;
            }}
            onTouchEnd={(e) => {
              const startX = e.currentTarget._touchStartX;
              if (startX === undefined) return;
              const diff = startX - e.changedTouches[0].clientX;
              if (Math.abs(diff) > 50) {
                if (diff > 0 && activeSlide < slidesData.length - 1) setActiveSlide(activeSlide + 1);
                if (diff < 0 && activeSlide > 0) setActiveSlide(activeSlide - 1);
              }
            }}
            style={{ backgroundColor: '#fff', border: '1px solid rgba(13,27,62,0.08)', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
            <div className="relative h-52 overflow-hidden">
              <img src={slidesData[activeSlide].image} loading="lazy"
                alt={isRo ? slidesData[activeSlide].titleRo : slidesData[activeSlide].titleEn}
                className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(13,27,62,0.5) 0%, transparent 60%)' }}></div>
            </div>
            <div className="p-5">
              <h3 className="font-display text-lg font-bold text-[#0D1B3E] mb-2">
                {isRo ? slidesData[activeSlide].titleRo : slidesData[activeSlide].titleEn}
              </h3>
              <p className="font-body text-sm text-gray-500 leading-relaxed mb-5">
                {isRo ? slidesData[activeSlide].descRo : slidesData[activeSlide].descEn}
              </p>
              <button onClick={goToContact}
                data-testid={`slide-cta-mobile-${slidesData[activeSlide].id}`}
                className="inline-flex items-center justify-center bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold px-6 h-12 text-sm rounded shadow-md hover:shadow-lg transition-all cursor-pointer w-full">
                {isRo ? slidesData[activeSlide].ctaRo : slidesData[activeSlide].ctaEn}
              </button>
            </div>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mt-6">
            {slidesData.map((_, i) => (
              <button key={i} onClick={() => setActiveSlide(i)}
                data-testid={`slide-dot-${i}`}
                className="w-3 h-3 rounded-full transition-all"
                style={{ backgroundColor: i === activeSlide ? '#10B981' : 'rgba(13,27,62,0.15)' }} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const StageIndicator = ({ stage }) => (
  <div className="flex items-center justify-center mb-8 gap-2">
    {[1, 2, 3].map((s) => (
      <div key={s} className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
          s === stage ? 'bg-[#10B981] text-white' : s < stage ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-gray-200 text-gray-400'
        }`}>
          {s < stage ? <CheckCircle2 size={16} /> : s}
        </div>
        {s < 3 && <div className={`w-8 sm:w-12 h-0.5 ${s < stage ? 'bg-[#10B981]' : 'bg-gray-200'}`} />}
      </div>
    ))}
  </div>
);

const TransfersPage = ({ language }) => {
  const isRo = language === 'ro';
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [selectedCarType, setSelectedCarType] = useState('standard');
  const [stage, setStage] = useState(1);
  const [tripType, setTripType] = useState('one-way');
  const [direction, setDirection] = useState('to-airport');
  const [formData, setFormData] = useState({
    date: null,
    time: '10:00',
    returnDate: null,
    returnTime: '10:00',
    address: '',
    fullName: '',
    email: '',
    phone: '',
    flightNumber: '',
    passengers: '1',
    luggage: '1',
    paymentMethod: 'card',
    notes: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToForm = () => {
    setTimeout(() => {
      document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const getPrice = (airport, carTypeId) => {
    const oneWay = airport.prices[carTypeId];
    return tripType === 'two-way' ? oneWay * 2 : oneWay;
  };

  const goToStage2 = () => {
    setError('');
    if (!selectedAirport || !formData.date) {
      setError(isRo ? 'Selectează ruta și data transferului.' : 'Please select route and transfer date.');
      return;
    }
    if (tripType === 'two-way' && !formData.returnDate) {
      setError(isRo ? 'Selectează și data pentru cursă retur.' : 'Please also select the return date.');
      return;
    }
    if (parseInt(formData.passengers) > 3) {
      setSelectedCarType('minivan');
    }
    setStage(2);
    scrollToForm();
  };

  const goToStage3 = () => {
    setError('');
    setStage(3);
    scrollToForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.fullName || !formData.phone) {
      setError(isRo ? 'Completează numele și telefonul.' : 'Please fill in name and phone.');
      return;
    }
    setLoading(true);
    try {
      const airport = airports.find(a => a.id === selectedAirport);
      const car = carTypes.find(c => c.id === selectedCarType);
      const price = getPrice(airport, selectedCarType);
      const dirLabel = direction === 'to-airport'
        ? `Brașov → ${airport.code}`
        : `${airport.code} → Brașov`;
      await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          message: [
            'TRANSFER REQUEST',
            `Route: ${dirLabel} (${isRo ? airport.nameRo : airport.nameEn})`,
            `Trip: ${tripType === 'two-way' ? 'Round trip / Dus-Întors' : 'One way / Doar dus'}`,
            `Car: ${car.label} (${car.desc})`,
            `Date: ${format(formData.date, 'PPP')}, ${formData.time}`,
            ...(tripType === 'two-way' && formData.returnDate
              ? [`Return: ${format(formData.returnDate, 'PPP')}, ${formData.returnTime}`]
              : []),
            `Flight: ${formData.flightNumber || '-'}`,
            `Address: ${formData.address || '-'}`,
            `Passengers: ${formData.passengers}`,
            `Luggage: ${formData.luggage}`,
            `Payment: ${formData.paymentMethod === 'card' ? 'Online card' : formData.paymentMethod === 'transfer' ? 'Bank transfer' : 'Cash'}`,
            `Price: ${price} RON`,
            `Notes: ${formData.notes || '-'}`,
          ].join('\n'),
        })
      });
      setSubmitted(true);
      // GTM: Track transfer booking conversion
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'booking_submitted',
        booking_type: 'transfer',
        route: dirLabel,
        vehicle_type: car.label,
        trip_type: tripType,
        booking_value: price,
        currency: 'RON',
      });
    } catch (err) {
      setError(isRo ? 'Eroare la trimitere. Încercați din nou.' : 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setStage(1);
    setSelectedAirport(null);
    setSelectedCarType('standard');
    setTripType('one-way');
    setDirection('to-airport');
    setFormData({ date: null, time: '10:00', returnDate: null, returnTime: '10:00', address: '', fullName: '', email: '', phone: '', flightNumber: '', passengers: '1', luggage: '1', notes: '' });
  };

  return (
    <>
      {/* ── Hero ── */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-28 sm:pt-0" data-testid="transfers-hero">
        <div className="absolute inset-0">
          <img src="/images/transfers-cover.webp" alt="Airport Transfers" className="w-full h-full object-cover" />
          <div className="absolute inset-0 md:hidden" style={{ backgroundColor: 'rgba(255,255,255,0.80)' }}></div>
          <div className="absolute inset-0 hidden md:block" style={{ background: 'linear-gradient(to right, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.90) 35%, rgba(255,255,255,0.60) 60%, rgba(255,255,255,0) 100%)' }}></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 relative z-10 py-20">
          <div className="max-w-2xl">
            <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              {isRo ? 'Transfer Aeroport & Transferuri Private' : 'Airport Transfers & Private Transfers'}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0D1B3E] mb-4 leading-tight">
              {isRo
                ? 'Transfer Aeroport Brașov & Transferuri Private în România'
                : 'Brașov Airport Transfers & Private Transfers in Romania'}
            </h1>
            <p className="font-body text-base text-gray-600 mb-8 leading-relaxed max-w-lg">
              {isRo
                ? 'Transferuri private către aeroport, între orașe, călătorii business și evenimente. Șoferi profesioniști, prețuri transparente și confort premium.'
                : 'Private transfers to airports, intercity, business trips and events. Professional drivers, transparent prices and premium comfort.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => scrollTo('booking-form')} data-testid="transfers-cta-primary"
                className="inline-flex items-center justify-center bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold px-8 h-14 text-base rounded shadow-lg hover:shadow-xl transition-all cursor-pointer">
                {isRo ? 'Rezervă Acum' : 'Book Now'}
              </button>
              <button onClick={() => scrollTo('prices')} data-testid="transfers-cta-secondary"
                className="inline-flex items-center justify-center bg-[#0D1B3E] hover:bg-[#1A3A6B] text-white font-semibold px-8 h-14 text-base rounded shadow-lg hover:shadow-xl transition-all cursor-pointer">
                {isRo ? 'Vezi Prețurile' : 'See Prices'}
              </button>
            </div>
          </div>
        </div>
      </section>

      <TrustRow isRo={isRo} />

      {/* ── Benefits Section ── */}
      <section className="py-20 sm:py-28 bg-white" data-testid="transfers-benefits">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              {isRo ? 'De Ce Să Alegi Montréon' : 'Why Choose Montréon'}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0D1B3E]">
              {isRo ? 'Beneficiile transferurilor noastre' : 'Benefits of our transfers'}
            </h2>
          </div>
          <style>{`
            .benefit-card { position: relative; overflow: hidden; transition: transform 0.3s ease, box-shadow 0.3s ease; }
            .benefit-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 50%; background: linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 100%); pointer-events: none; border-radius: 12px 12px 0 0; z-index: 1; }
            .benefit-card::after { content: ''; position: absolute; top: -1px; left: 20%; right: 20%; height: 1px; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent); z-index: 2; }
            .benefit-card:hover { transform: translateY(-4px); }
          `}</style>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="benefit-card rounded-xl p-7 text-center" data-testid={`benefit-card-${i}`}
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid rgba(13, 27, 62, 0.12)', boxShadow: '0 4px 24px rgba(13, 27, 62, 0.10), 0 1px 3px rgba(13, 27, 62, 0.08), inset 0 1px 0 rgba(255,255,255,0.8)' }}>
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: 'rgba(16, 185, 129, 0.10)' }}>
                    <Icon className="text-[#10B981]" size={26} />
                  </div>
                  <h3 className="font-display text-base font-bold text-[#0D1B3E] mb-2">{isRo ? b.titleRo : b.titleEn}</h3>
                  <p className="font-body text-sm text-gray-500 leading-relaxed">{isRo ? b.descRo : b.descEn}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Prices Section ── */}
      <section id="prices" className="py-20 sm:py-28" style={{ backgroundColor: '#F8FAFC' }} data-testid="transfers-prices">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              {isRo ? 'Prețuri Transferuri' : 'Transfer Prices'}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0D1B3E] mb-4">
              {isRo ? 'Alege ruta ta' : 'Choose your route'}
            </h2>
            <p className="font-body text-base text-gray-500 max-w-xl mx-auto">
              {isRo
                ? 'Toate prețurile sunt per cursă, doar dus, din Brașov către destinație.'
                : 'All prices are per trip, one way, from Brașov to destination.'}
            </p>
          </div>

          {/* Car Type Selector */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-12">
            {carTypes.map((car) => (
              <button key={car.id} onClick={() => setSelectedCarType(car.id)} data-testid={`car-type-${car.id}`}
                className="w-full sm:w-auto px-6 py-4 rounded-xl text-center transition-all"
                style={selectedCarType === car.id
                  ? { backgroundColor: '#0D1B3E', color: '#fff', boxShadow: '0 8px 30px rgba(13,27,62,0.25)' }
                  : { backgroundColor: 'rgba(255,255,255,1)', color: '#0D1B3E', border: '1px solid rgba(13,27,62,0.12)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }
                }>
                <span className="font-display text-sm font-bold tracking-wide block">{car.label}</span>
                <span className="text-xs block mt-1" style={{ opacity: 0.7 }}>{car.desc} · {isRo ? car.descFull : car.descFullEn}</span>
              </button>
            ))}
          </div>

          {/* Airport Price Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {airports.map((airport) => {
              const price = airport.prices[selectedCarType];
              const isSelected = selectedAirport === airport.id;
              return (
                <div key={airport.id} onClick={() => { setSelectedAirport(airport.id); scrollTo('booking-form'); }}
                  data-testid={`airport-card-${airport.id}`} className="cursor-pointer rounded-xl p-6 transition-all group"
                  style={isSelected
                    ? { backgroundColor: '#ECFDF5', border: '2px solid #10B981', boxShadow: '0 8px 30px rgba(16,185,129,0.15)' }
                    : { backgroundColor: '#fff', border: '2px solid transparent', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }
                  }
                  onMouseEnter={(e) => { if (!isSelected) { e.currentTarget.style.boxShadow = '0 8px 30px rgba(13,27,62,0.10)'; e.currentTarget.style.borderColor = 'rgba(16,185,129,0.4)'; } }}
                  onMouseLeave={(e) => { if (!isSelected) { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.05)'; e.currentTarget.style.borderColor = 'transparent'; } }}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(16, 185, 129, 0.10)' }}>
                      <Plane className="text-[#10B981]" size={22} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-xs font-bold text-[#10B981] tracking-wider uppercase mb-1">{airport.code}</p>
                      <h3 className="font-display text-sm font-bold text-[#0D1B3E] leading-snug mb-3">{isRo ? airport.nameRo : airport.nameEn}</h3>
                      <div className="flex items-baseline gap-1">
                        <span className="font-display text-2xl font-bold text-[#0D1B3E]">{price}</span>
                        <span className="text-gray-400 text-sm font-medium">RON</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Disclaimer */}
          <p className="text-center text-sm italic text-gray-400 mt-10 max-w-3xl mx-auto" data-testid="prices-disclaimer">
            {isRo
              ? '* Pentru zonele limitrofe din zona Brașovului sau pentru alte destinații prețul este diferit față de cele expuse pentru Brașov. Nu ezitați să ne contactați pentru o ofertă de preț personalizată.'
              : '* For areas surrounding Brașov or other destinations, prices may differ from those listed for Brașov. Do not hesitate to contact us for a personalized quote.'}
          </p>
        </div>
      </section>

      {/* ── 3-Stage Booking Form ── */}
      <section id="booking-form" className="py-20 sm:py-28 bg-[#0D1B3E]" data-testid="transfers-booking-form">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              {isRo ? 'Rezervare Transfer' : 'Book Transfer'}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
              {isRo ? 'Rezervă Transferul Tău' : 'Book Your Transfer'}
            </h2>
          </div>

          <div className={`mx-auto ${stage === 2 ? 'max-w-4xl' : 'max-w-2xl'}`}>
            <div className="bg-white rounded-xl shadow-2xl">
              <div className="p-6 sm:p-10">
                {submitted ? (
                  <div className="text-center py-12" data-testid="transfer-success">
                    <CheckCircle2 className="mx-auto text-[#10B981] mb-6" size={56} strokeWidth={1.5} />
                    <p className="font-body text-lg text-gray-700 max-w-md mx-auto">
                      {isRo
                        ? 'Vă mulțumim că ați ales serviciile noastre. Echipa noastră vă va contacta în cel mai scurt timp posibil pentru confirmarea rezervării.'
                        : 'Thank you for choosing our services. Our team will contact you as soon as possible to confirm your booking.'}
                    </p>
                    <button onClick={resetForm} className="mt-6 text-[#10B981] text-sm font-medium hover:underline" data-testid="new-booking-btn">
                      {isRo ? 'Rezervare nouă' : 'New booking'}
                    </button>
                  </div>
                ) : (
                  <>
                    <StageIndicator stage={stage} />

                    {/* ===== STAGE 1: Route, Direction, Date & Time ===== */}
                    {stage === 1 && (
                      <div className="space-y-5" data-testid="booking-stage-1">
                        {/* Trip Type */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {isRo ? 'Tip Cursă' : 'Trip Type'}
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            <button type="button" onClick={() => setTripType('one-way')} data-testid="form-trip-one-way"
                              className="flex items-center justify-center gap-2 p-3 rounded-lg border-2 text-sm font-medium transition-all"
                              style={tripType === 'one-way'
                                ? { borderColor: '#10B981', backgroundColor: '#ECFDF5', color: '#0D1B3E' }
                                : { borderColor: '#e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280' }
                              }>
                              <ArrowRight size={16} />
                              {isRo ? 'Doar Dus' : 'One Way'}
                            </button>
                            <button type="button" onClick={() => setTripType('two-way')} data-testid="form-trip-two-way"
                              className="flex items-center justify-center gap-2 p-3 rounded-lg border-2 text-sm font-medium transition-all"
                              style={tripType === 'two-way'
                                ? { borderColor: '#10B981', backgroundColor: '#ECFDF5', color: '#0D1B3E' }
                                : { borderColor: '#e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280' }
                              }>
                              <ArrowRightLeft size={16} />
                              {isRo ? 'Dus-Întors' : 'Round Trip'}
                            </button>
                          </div>
                        </div>

                        {/* Direction */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {isRo ? 'Direcție' : 'Direction'}
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button type="button" onClick={() => setDirection('to-airport')} data-testid="direction-to-airport"
                              className="flex items-center gap-3 p-3 rounded-lg border-2 text-sm font-medium transition-all text-left"
                              style={direction === 'to-airport'
                                ? { borderColor: '#10B981', backgroundColor: '#ECFDF5', color: '#0D1B3E' }
                                : { borderColor: '#e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280' }
                              }>
                              <MapPin size={16} className="flex-shrink-0" />
                              {isRo ? 'Brașov → Aeroport' : 'Brașov → Airport'}
                            </button>
                            <button type="button" onClick={() => setDirection('from-airport')} data-testid="direction-from-airport"
                              className="flex items-center gap-3 p-3 rounded-lg border-2 text-sm font-medium transition-all text-left"
                              style={direction === 'from-airport'
                                ? { borderColor: '#10B981', backgroundColor: '#ECFDF5', color: '#0D1B3E' }
                                : { borderColor: '#e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280' }
                              }>
                              <Plane size={16} className="flex-shrink-0" />
                              {isRo ? 'Aeroport → Brașov' : 'Airport → Brașov'}
                            </button>
                          </div>
                        </div>

                        {/* Airport Selection */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {isRo ? 'Aeroport' : 'Airport'} *
                          </label>
                          <Select onValueChange={setSelectedAirport} value={selectedAirport || ''}>
                            <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12" data-testid="select-airport">
                              <SelectValue placeholder={isRo ? 'Selectează aeroportul' : 'Select airport'} />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200 rounded-lg shadow-lg">
                              {airports.map((airport) => (
                                <SelectItem key={airport.id} value={airport.id} className="text-gray-900 hover:bg-gray-100 rounded-lg">
                                  {airport.code} — {isRo ? airport.nameRo : airport.nameEn}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Date & Time */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {tripType === 'two-way'
                              ? (isRo ? 'Data și Ora — Dus' : 'Date & Time — Outbound')
                              : (isRo ? 'Data' : 'Date')} *
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" data-testid="select-date"
                                  className="w-full justify-start text-left bg-gray-50 border-gray-200 text-gray-900 hover:bg-gray-100 rounded-lg h-12">
                                  <CalendarIcon className="mr-3 h-4 w-4 text-[#10B981]" />
                                  {formData.date ? format(formData.date, 'PPP') : <span className="text-gray-400">{isRo ? 'Selectează data' : 'Select date'}</span>}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0 bg-white border-gray-200 rounded-lg shadow-lg">
                                <Calendar mode="single" selected={formData.date}
                                  onSelect={(date) => setFormData({...formData, date})}
                                  disabled={(date) => date < new Date()} className="bg-white text-gray-900" />
                              </PopoverContent>
                            </Popover>
                            <Input type="time" value={formData.time}
                              onChange={(e) => setFormData({...formData, time: e.target.value})}
                              className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12" data-testid="input-time" />
                          </div>
                        </div>

                        {/* Return Date & Time (only for Dus-Întors) */}
                        {tripType === 'two-way' && (
                          <div>
                            <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                              {isRo ? 'Data și Ora — Retur' : 'Date & Time — Return'} *
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" data-testid="select-return-date"
                                    className="w-full justify-start text-left bg-gray-50 border-gray-200 text-gray-900 hover:bg-gray-100 rounded-lg h-12">
                                    <CalendarIcon className="mr-3 h-4 w-4 text-[#10B981]" />
                                    {formData.returnDate ? format(formData.returnDate, 'PPP') : <span className="text-gray-400">{isRo ? 'Selectează data retur' : 'Select return date'}</span>}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0 bg-white border-gray-200 rounded-lg shadow-lg">
                                  <Calendar mode="single" selected={formData.returnDate}
                                    onSelect={(date) => setFormData({...formData, returnDate: date})}
                                    disabled={(date) => date < (formData.date || new Date())} className="bg-white text-gray-900" />
                                </PopoverContent>
                              </Popover>
                              <Input type="time" value={formData.returnTime}
                                onChange={(e) => setFormData({...formData, returnTime: e.target.value})}
                                className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12" data-testid="input-return-time" />
                            </div>
                          </div>
                        )}

                        {/* Address */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {direction === 'to-airport'
                              ? (isRo ? 'Adresa de Preluare (Brașov)' : 'Pick-up Address (Brașov)')
                              : (isRo ? 'Adresa de Destinație (Brașov)' : 'Destination Address (Brașov)')}
                          </label>
                          <Input value={formData.address}
                            onChange={(e) => setFormData({...formData, address: e.target.value})}
                            className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12"
                            placeholder={isRo ? 'Hotel, adresă, punct de interes...' : 'Hotel, address, point of interest...'}
                            data-testid="input-address" />
                        </div>

                        {error && (
                          <div className="flex items-center space-x-3 text-red-500 bg-red-50 border border-red-200 p-4 rounded-lg" data-testid="form-error">
                            <AlertCircle size={18} />
                            <span className="text-sm">{error}</span>
                          </div>
                        )}

                        <Button onClick={goToStage2} data-testid="booking-stage1-cta"
                          className="w-full bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold py-6 text-base rounded-lg transition-all">
                          {isRo ? 'Alege Vehiculul' : 'Choose Vehicle'}
                          <ArrowRight className="ml-2" size={18} />
                        </Button>
                      </div>
                    )}

                    {/* ===== STAGE 2: Car Selection ===== */}
                    {stage === 2 && (
                      <div className="space-y-6" data-testid="booking-stage-2">
                        <button type="button" onClick={() => { setStage(1); setError(''); scrollToForm(); }}
                          className="flex items-center text-sm text-gray-500 hover:text-[#10B981] transition-colors mb-2">
                          <ArrowLeft size={16} className="mr-1" />
                          {isRo ? 'Înapoi la rută' : 'Back to route'}
                        </button>

                        {/* Route Summary */}
                        {selectedAirport && (() => {
                          const airport = airports.find(a => a.id === selectedAirport);
                          const dirLabel = direction === 'to-airport'
                            ? `Brașov → ${airport.code}`
                            : `${airport.code} → Brașov`;
                          return (
                            <div className="p-4 rounded-lg flex flex-wrap items-center gap-3 text-sm" style={{ backgroundColor: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
                              <span className="font-bold text-[#0D1B3E]">{dirLabel}</span>
                              <span className="text-gray-400">·</span>
                              <span className="text-gray-600">{formData.date && format(formData.date, 'dd MMM yyyy')}, {formData.time}</span>
                              {tripType === 'two-way' && formData.returnDate && (
                                <>
                                  <span className="text-gray-400">·</span>
                                  <span className="text-gray-600">{isRo ? 'Retur' : 'Return'}: {format(formData.returnDate, 'dd MMM yyyy')}, {formData.returnTime}</span>
                                </>
                              )}
                              <span className="text-gray-400">·</span>
                              <span className="text-gray-600">{tripType === 'two-way' ? (isRo ? 'Dus-Întors' : 'Round Trip') : (isRo ? 'Doar Dus' : 'One Way')}</span>
                            </div>
                          );
                        })()}

                        <p className="text-sm text-gray-500">
                          {isRo ? 'Selectează vehiculul preferat:' : 'Select your preferred vehicle:'}
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          {carTypes.filter(car => car.maxPax >= parseInt(formData.passengers)).map((car) => {
                            const airport = airports.find(a => a.id === selectedAirport);
                            const price = airport ? getPrice(airport, car.id) : 0;
                            const isSelected = selectedCarType === car.id;
                            return (
                              <div key={car.id} onClick={() => setSelectedCarType(car.id)}
                                data-testid={`form-car-${car.id}`}
                                className="cursor-pointer rounded-xl border-2 p-5 transition-all text-center"
                                style={isSelected
                                  ? { borderColor: '#10B981', backgroundColor: '#ECFDF5', boxShadow: '0 8px 30px rgba(16,185,129,0.15)' }
                                  : { borderColor: '#e5e7eb', backgroundColor: '#fff' }
                                }>
                                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                                  style={{ backgroundColor: isSelected ? 'rgba(16,185,129,0.15)' : 'rgba(13,27,62,0.06)' }}>
                                  {car.id === 'minivan'
                                    ? <Users size={22} className={isSelected ? 'text-[#10B981]' : 'text-gray-400'} />
                                    : <Car size={22} className={isSelected ? 'text-[#10B981]' : 'text-gray-400'} />}
                                </div>
                                <h3 className="font-display text-sm font-bold text-[#0D1B3E] mb-0.5">{car.label}</h3>
                                <p className="text-xs text-gray-500 mb-1">{car.desc}</p>
                                <p className="text-xs text-gray-400 mb-3">{isRo ? car.descFull : car.descFullEn}</p>
                                <div className="pt-3" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                                  <span className="font-display text-2xl font-bold text-[#0D1B3E]">{price}</span>
                                  <span className="text-gray-400 text-sm ml-1">RON</span>
                                </div>
                                {tripType === 'two-way' && (
                                  <p className="text-xs text-gray-400 mt-1">{isRo ? 'dus-întors' : 'round trip'}</p>
                                )}
                                {isSelected && <CheckCircle2 size={18} className="text-[#10B981] mx-auto mt-3" />}
                              </div>
                            );
                          })}
                        </div>

                        {error && (
                          <div className="flex items-center space-x-3 text-red-500 bg-red-50 border border-red-200 p-4 rounded-lg" data-testid="form-error">
                            <AlertCircle size={18} />
                            <span className="text-sm">{error}</span>
                          </div>
                        )}

                        <Button onClick={goToStage3} data-testid="booking-stage2-cta"
                          className="w-full bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold py-6 text-base rounded-lg transition-all">
                          {isRo ? 'Continuă' : 'Continue'}
                          <ArrowRight className="ml-2" size={18} />
                        </Button>
                      </div>
                    )}

                    {/* ===== STAGE 3: Personal Details ===== */}
                    {stage === 3 && (
                      <form onSubmit={handleSubmit} className="space-y-5" data-testid="booking-stage-3">
                        <button type="button" onClick={() => { setStage(2); setError(''); scrollToForm(); }}
                          className="flex items-center text-sm text-gray-500 hover:text-[#10B981] transition-colors mb-2">
                          <ArrowLeft size={16} className="mr-1" />
                          {isRo ? 'Înapoi la vehicul' : 'Back to vehicle'}
                        </button>

                        {/* Name */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {isRo ? 'Nume Complet' : 'Full Name'} *
                          </label>
                          <Input value={formData.fullName} onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                            className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12"
                            placeholder={isRo ? 'Nume și prenume' : 'Full name'} data-testid="input-fullname" />
                        </div>

                        {/* Phone & Email */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div>
                            <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                              {isRo ? 'Telefon' : 'Phone'} *
                            </label>
                            <Input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                              className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12"
                              placeholder="+40 7XX XXX XXX" data-testid="input-phone" />
                          </div>
                          <div>
                            <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">Email</label>
                            <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                              className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12"
                              placeholder="email@example.com" data-testid="input-email" />
                          </div>
                        </div>

                        {/* Flight Details */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {isRo ? 'Detalii Zbor (Număr Zbor)' : 'Flight Details (Flight Number)'}
                          </label>
                          <Input value={formData.flightNumber} onChange={(e) => setFormData({...formData, flightNumber: e.target.value})}
                            className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12"
                            placeholder={isRo ? 'ex. W6 3152' : 'e.g. W6 3152'} data-testid="input-flight" />
                        </div>

                        {/* Passengers & Luggage */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div>
                            <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                              {isRo ? 'Număr Pasageri' : 'Passengers'}
                            </label>
                            <Select onValueChange={(val) => setFormData({...formData, passengers: val})} value={formData.passengers}>
                              <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12" data-testid="select-passengers">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-gray-200 rounded-lg shadow-lg">
                                {[1,2,3,4,5,6,7].map(n => (
                                  <SelectItem key={n} value={n.toString()} className="text-gray-900 hover:bg-gray-100 rounded-lg">
                                    {n} {n === 1 ? (isRo ? 'pasager' : 'passenger') : (isRo ? 'pasageri' : 'passengers')}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                              {isRo ? 'Număr Bagaje' : 'Luggage Count'}
                            </label>
                            <Select onValueChange={(val) => setFormData({...formData, luggage: val})} value={formData.luggage}>
                              <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900 rounded-lg h-12" data-testid="select-luggage">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-gray-200 rounded-lg shadow-lg">
                                {[0,1,2,3,4,5,6,7,8].map(n => (
                                  <SelectItem key={n} value={n.toString()} className="text-gray-900 hover:bg-gray-100 rounded-lg">
                                    {n} {n === 1 ? (isRo ? 'bagaj' : 'bag') : (isRo ? 'bagaje' : 'bags')}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Payment Method */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {isRo ? 'Metoda de Plată' : 'Payment Method'}
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {[
                              { id: 'card', labelRo: 'Plata online cu card', labelEn: 'Online card payment', icon: CreditCard },
                              { id: 'transfer', labelRo: 'Transfer bancar', labelEn: 'Bank transfer', icon: Landmark },
                              { id: 'cash', labelRo: 'Cash', labelEn: 'Cash', icon: Banknote },
                            ].map((pm) => {
                              const PmIcon = pm.icon;
                              return (
                                <button type="button" key={pm.id} onClick={() => setFormData({...formData, paymentMethod: pm.id})}
                                  data-testid={`payment-${pm.id}`}
                                  className="flex items-center gap-3 p-3 rounded-lg border-2 text-sm font-medium transition-all text-left"
                                  style={formData.paymentMethod === pm.id
                                    ? { borderColor: '#10B981', backgroundColor: '#ECFDF5', color: '#0D1B3E' }
                                    : { borderColor: '#e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280' }
                                  }>
                                  <PmIcon size={18} className="flex-shrink-0" />
                                  <span>{isRo ? pm.labelRo : pm.labelEn}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Notes */}
                        <div>
                          <label className="block font-body text-sm text-gray-600 mb-2 tracking-wide uppercase">
                            {isRo ? 'Observații (opțional)' : 'Notes (optional)'}
                          </label>
                          <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})}
                            className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-lg p-3 h-24 resize-none focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981] text-sm"
                            placeholder={isRo ? 'Detalii suplimentare...' : 'Additional details...'}
                            data-testid="input-notes" />
                        </div>

                        {error && (
                          <div className="flex items-center space-x-3 text-red-500 bg-red-50 border border-red-200 p-4 rounded-lg" data-testid="form-error">
                            <AlertCircle size={18} />
                            <span className="text-sm">{error}</span>
                          </div>
                        )}

                        {/* Summary — moved above submit */}
                        {selectedAirport && (() => {
                          const airport = airports.find(a => a.id === selectedAirport);
                          const car = carTypes.find(c => c.id === selectedCarType);
                          const price = getPrice(airport, selectedCarType);
                          const dirLabel = direction === 'to-airport' ? `Brașov → ${airport.code}` : `${airport.code} → Brașov`;
                          return (
                            <div className="p-4 bg-[#0D1B3E] rounded-lg" data-testid="price-summary">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div>
                                  <p className="text-sm font-medium text-white">{dirLabel}</p>
                                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                                    {car.label} ({car.desc}) · {tripType === 'two-way' ? (isRo ? 'Dus-Întors' : 'Round Trip') : (isRo ? 'Doar Dus' : 'One Way')}
                                  </p>
                                </div>
                                <span className="text-[#10B981] font-bold text-2xl">{price} RON</span>
                              </div>
                            </div>
                          );
                        })()}

                        <Button type="submit" disabled={loading} data-testid="transfer-submit"
                          className="w-full bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold py-6 text-base rounded-lg transition-all">
                          {loading
                            ? (isRo ? 'Se procesează...' : 'Processing...')
                            : (isRo ? 'Rezervă Acum' : 'Book Now')}
                        </Button>
                      </form>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Business / Corporate / Events Slides ── */}
      <TransferSlides isRo={isRo} />

      <WhyChoose isRo={isRo} />
      <OurServices isRo={isRo} />
      <GoogleReviews isRo={isRo} />
      <TransfersFAQ isRo={isRo} />
    </>
  );
};

export default TransfersPage;
